import { useMemo, useState } from 'react'
import type { Task } from '@friday/shared/realtime'
import { useBoard, useCreateTask, useUpdateTask } from '@/features/projects/board'
import { useProfiles, pickDisplay } from '@/features/profiles/profiles'
import { sortTasks, type BoardSort, type SortContext } from '@/features/projects/sorting'
import { AssigneeChip } from '@/components/user/AssigneeChip'
import { toast } from '@/features/feedback/toast'

/** Touch-first grouped board; all writes use the existing versioned API. */
export function MobileBoard({
  wsId,
  pId,
  sprintScope,
  onOpenTask,
  sort,
}: {
  wsId: string
  pId: string
  sprintScope: string | null
  onOpenTask: (task: Task) => void
  /** The sort the board shows (FRIDAY-75). */
  sort: BoardSort
}) {
  const board = useBoard(wsId, pId)
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [title, setTitle] = useState('')
  const [columnId, setColumnId] = useState('')
  const columns = board.data?.columns ?? []
  const target = columns.find((c) => c.id === columnId)?.id ?? columns[0]?.id
  const create = useCreateTask(wsId, pId, target)
  const update = useUpdateTask(wsId, pId)
  const tasks = (board.data?.tasks ?? []).filter(
    (t) =>
      (sprintScope === 'all' || t.sprintId === sprintScope) &&
      `${t.title} ${t.key}`.toLowerCase().includes(search.toLowerCase().trim()),
  )
  const assigneeIds = useMemo(
    () => [...new Set(tasks.map((t) => t.assigneeUserId).filter((id): id is string => !!id))],
    [tasks],
  )
  const { profiles } = useProfiles(assigneeIds)
  const sortContext: SortContext = useMemo(
    () => ({
      nameOf: (userId) => pickDisplay(userId, profiles[userId]).displayName,
      columnRank: (columnId) => columns.find((c) => c.id === columnId)?.position ?? 0,
      sprintName: (sprintId) =>
        sprintId ? ((board.data?.sprints ?? []).find((s) => s.id === sprintId)?.name ?? null) : null,
      milestoneName: (milestoneId) =>
        milestoneId
          ? ((board.data?.milestones ?? []).find((m) => m.id === milestoneId)?.name ?? null)
          : null,
    }),
    [profiles, columns, board.data],
  )
  return (
    <section className="mobile-board bg-app-subtle h-full overflow-y-auto p-3" aria-label="Mobile task board">
      <label className="block">
        <span className="sr-only">Search board</span>
        <input
          type="search"
          className="input mb-3"
          placeholder="Search this board…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>
      <form
        className="border-app-border bg-app mb-4 flex flex-wrap gap-2 rounded-xl border p-3"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!title.trim() || !target || create.isPending) return
          try {
            await create.mutateAsync({ title: title.trim(), priority: 'medium', sprintId: sprintScope === 'all' ? null : sprintScope })
            setTitle('')
            toast('Task created', { kind: 'success' })
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not create task', { kind: 'error' })
          }
        }}
      >
        <input
          aria-label="New task title"
          className="input"
          placeholder="+ Add a task"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <select
          aria-label="New task group"
          className="border-app-border bg-app min-w-0 flex-1 rounded border px-2"
          value={target ?? ''}
          onChange={(e) => setColumnId(e.target.value)}
        >
          {columns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button className="btn-primary" disabled={!title.trim() || !target || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add task'}
        </button>
      </form>
      <p className="text-surface-500 mb-3 text-xs" role="status">
        {tasks.length} matching tasks
      </p>
      {columns.map((column) => {
        const group = sortTasks(
          tasks.filter((t) => t.columnId === column.id),
          sort.key,
          sort.direction,
          sortContext,
        )
        const closed = collapsed.has(column.id)
        return (
          <section
            key={column.id}
            className="border-app-border bg-app mb-4 overflow-hidden rounded-xl border"
            style={{ borderTop: `3px solid ${column.color}` }}
          >
            <button
              className="flex w-full items-center gap-2 px-3 py-3 text-left font-semibold"
              aria-expanded={!closed}
              onClick={() =>
                setCollapsed((previous) => {
                  const next = new Set(previous)
                  next.has(column.id) ? next.delete(column.id) : next.add(column.id)
                  return next
                })
              }
            >
              <span aria-hidden>{closed ? '›' : '⌄'}</span>
              {column.name}
              <span className="bg-app-subtle text-surface-500 ml-auto rounded-full px-2 text-xs">
                {group.length}
              </span>
            </button>
            {!closed && (
              <div>
                {group.map((task) => (
                  <article key={task.id} className="border-app-border border-t px-3 py-2">
                    <button className="w-full py-2 text-left" onClick={() => onOpenTask(task)}>
                      <span className="text-surface-500 block text-[11px]">
                        {task.key}
                        {task.subtaskCount > 0
                          ? ` · ${task.subtaskDoneCount}/${task.subtaskCount} subtasks`
                          : ''}
                      </span>
                      <span className="mt-1 block break-words text-sm font-medium">{task.title}</span>
                    </button>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <select
                        aria-label={`Status for ${task.key}`}
                        value={task.columnId}
                        disabled={update.isPending}
                        className="border-app-border bg-app-subtle min-w-0 max-w-full rounded-md border px-2"
                        onChange={async (e) => {
                          try {
                            await update.mutateAsync({
                              taskId: task.id,
                              version: task.version,
                              patch: { columnId: e.target.value },
                            })
                          } catch {
                            toast('Could not change status. Please try again.', { kind: 'error' })
                          }
                        }}
                      >
                        {columns.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <span className="bg-app-subtle rounded-full px-2 py-1 capitalize">{task.priority}</span>
                      {task.dueDate && (
                        <span className="text-surface-500">Due {task.dueDate.slice(0, 10)}</span>
                      )}
                      <AssigneeChip userId={task.assigneeUserId} profiles={profiles} size="xs" />
                    </div>
                  </article>
                ))}
                {group.length === 0 && (
                  <p className="text-surface-500 px-3 pb-4 text-xs">
                    {search ? 'No matching tasks in this group.' : 'No tasks in this group yet.'}
                  </p>
                )}
              </div>
            )}
          </section>
        )
      })}
    </section>
  )
}
